import Avatar from '@components/v2/ui/Avatar/Avatar';
import ListItemAvatar from '@components/v2/ui/List/ListItemAvatar';
import ListItemText from '@components/v2/ui/List/ListItemText';
import ListSubheader from '@components/v2/ui/List/ListSubheader';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import { Divider } from '@mui/material';
import LangEnum from '../../../../lang/LangEnum';
import { Brand } from '../../../../modules/context/ApplicationContext/applicationContext.types';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { BrandSelectorTestIds } from '../../../../utils/testIds/brandSelector.testIds';

interface BrandSelectorMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  isInBrandEnvironment: boolean;
  storeName: string;
  storeLogoUrl?: string;
  storeInitial: string;
  brands: Brand[];
  currentBrandId?: string;
  onBrandClick: (brand: Brand) => void;
  onGoToStore: () => void;
}

const BrandSelectorMenu = ({
  anchorEl,
  onClose,
  isInBrandEnvironment,
  storeName,
  storeLogoUrl,
  storeInitial,
  brands,
  currentBrandId,
  onBrandClick,
  onGoToStore,
}: BrandSelectorMenuProps) => {
  const { translate } = useTranslation();

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      elevation={2}
      slotProps={{
        paper: {
          sx: { width: 260, maxHeight: 700, overflowY: 'auto' },
        },
      }}
    >
      <ListSubheader>{translate(LangEnum.BRAND_SELECTOR_WORKSPACE_LABEL)}</ListSubheader>
      <MenuItem selected={!isInBrandEnvironment} onClick={onGoToStore}>
        <ListItemAvatar>
          <Avatar src={storeLogoUrl} alt={storeName} fallback="initials" initials={storeInitial} />
        </ListItemAvatar>
        <ListItemText
          primary={`${storeName} (${translate(LangEnum.BRAND_SELECTOR_STORE_LABEL)})`}
          slotProps={{ primary: { noWrap: true } }}
        />
      </MenuItem>
      <Divider />

      {brands.length > 0 ? (
        brands.map((brand) => (
          <MenuItem
            key={brand.id}
            selected={brand.id === currentBrandId}
            testId={`${BrandSelectorTestIds.BRAND_ITEM_PREFIX}${brand.id}`}
            onClick={() => onBrandClick(brand)}
          >
            <ListItemAvatar>
              <Avatar
                src={brand.logo}
                alt={brand.name}
                fallback="initials"
                initials={brand.name[0]?.toUpperCase() ?? ''}
              />
            </ListItemAvatar>
            <ListItemText primary={brand.name} slotProps={{ primary: { noWrap: true } }} />
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>
          <ListItemText
            primary={translate(LangEnum.BRAND_SELECTOR_NO_BRANDS)}
            slotProps={{ primary: { textAlign: 'center' } }}
          />
        </MenuItem>
      )}
    </Menu>
  );
};

export default BrandSelectorMenu;
